// Reaching a document without leaving the form.
//
// Two components and one decision, and the decision is documentOpening() in
// documents.ts rather than anything either of these works out for itself.
//
// The failure this exists to prevent is mundane and expensive. Somebody half
// way through a registration taps "Official Rules", the rules open as a page,
// and on a kiosk tablet there is no address bar and no back gesture that goes
// anywhere. The form is gone. Either a crew member notices and restarts them,
// or they walk off. Either way the activation lost a registration to a link
// that was put there for their protection.

import { useEffect, useId, useRef, type ReactNode } from "react";
import { compileDocumentBody } from "@nova/xp-schema";
import { documentOpening, type AgreementDocument } from "./documents.js";
import type { CaptureSurface } from "../activation.js";

export type DocumentLinkProps = {
  document: AgreementDocument;
  surface: CaptureSurface;
  /** Called on a station, where the caller opens the modal. */
  onOpen: (document: AgreementDocument) => void;
  disabled?: boolean | undefined;
};

/**
 * The inline link beside a control.
 *
 * An anchor on an attendee's own device and a button on ours, because they are
 * genuinely different acts. An anchor with a click handler that opens a dialog
 * lies to a screen reader, to a long press and to anybody who middle-clicks.
 */
export function DocumentLink({ document, surface, onOpen, disabled }: DocumentLinkProps): ReactNode {
  if (documentOpening(surface) === "new_tab") {
    return (
      <a
        className="xp-doclink"
        href={document.href ?? "#"}
        target="_blank"
        // noopener because a new tab we hand a page can otherwise reach back
        // through window.opener and navigate the form it came from, which is
        // the one thing this whole arrangement is trying to stop.
        rel="noopener noreferrer"
      >
        {document.title}
        <span className="xp-doclink__hint"> (opens in a new tab)</span>
      </a>
    );
  }

  return (
    <button
      type="button"
      className="xp-doclink xp-doclink--modal"
      disabled={disabled === true}
      onClick={() => onOpen(document)}
    >
      {document.title}
    </button>
  );
}

export type DocumentModalProps = {
  document: AgreementDocument;
  onClose: () => void;
  /**
   * Whether the popup may offer the document's public URL as a second control.
   *
   * The same argument that puts a modal on a station rather than a page: the
   * link opens a new tab, and a tablet in kiosk mode has no way back from one.
   * Offering it there is offering the exit this component exists to remove, so
   * a surface we own passes "hide". Defaults to showing it, because a reader
   * on their own phone loses nothing by having it.
   */
  publicLink?: "show" | "hide" | undefined;
};

/**
 * The document, over the form, on a surface we own.
 *
 * Three properties, and each is the reason for a line below.
 *
 * The form stays mounted underneath. Nothing about opening this touches the
 * half-finished registration, so closing it puts the person back exactly where
 * they were, with what they had typed still there.
 *
 * Escape closes it and focus starts inside it. A kiosk has a keyboard attached
 * often enough, and a crew member reading the rules aloud to somebody is a real
 * thing that happens.
 *
 * The body is what the popup shows. A public URL, when present and when the
 * surface can afford an exit, is an extra link and is never loaded in place of
 * the body. See `publicLink`. Markup is the closed markdown
 * subset compiled to a short HTML allowlist (compileDocumentBody); anything
 * else is stripped rather than interpreted.
 */
export function DocumentModal({ document, onClose, publicLink = "show" }: DocumentModalProps): ReactNode {
  const titleId = useId();
  const panel = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    panel.current?.focus();
  }, []);

  return (
    <div
      className="xp-modal"
      onClick={(event) => {
        // The backdrop only. A click inside the panel is somebody selecting
        // text in a document they are being asked to agree to.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="xp-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        ref={panel}
        onKeyDown={(event) => {
          if (event.key === "Escape") onClose();
        }}
      >
        {/* Head and foot sit outside the scrolling region deliberately. When the
            whole panel scrolled, a long set of rules pushed both the title and
            the way out off the top and bottom, so the one control somebody
            needs after reading was the one they had to scroll to find. */}
        <div className="xp-modal__head">
          <div>
            <h2 className="xp-modal__title" id={titleId}>{document.title}</h2>
            <p className="xp-modal__version">Version {document.version}</p>
          </div>
          {/* Two ways out rather than one. The corner is where a hand goes on a
              tablet and is reachable without reading to the end; the button at
              the foot is where somebody arrives having read. Both are the same
              act, so both say Close to a screen reader. */}
          <button
            type="button"
            className="xp-modal__x"
            onClick={onClose}
            aria-label={`Close ${document.title}`}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true" focusable="false">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <DocumentBody document={document} />
        {document.href === undefined || publicLink === "hide" ? null : (
          <p className="xp-modal__href">
            <a href={document.href} target="_blank" rel="noopener noreferrer">
              Open the public page
            </a>
          </p>
        )}
        <button type="button" className="xp-btn xp-btn--primary xp-modal__done" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

function DocumentBody({ document }: { readonly document: AgreementDocument }): ReactNode {
  const html = compileDocumentBody(document.body);
  if (!html.includes("<")) {
    return <div className="xp-modal__body">{html}</div>;
  }
  return (
    <div
      className="xp-modal__body xp-modal__body--html"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export type LegalFooterProps = {
  documents: readonly AgreementDocument[];
  surface: CaptureSurface;
  onOpen: (document: AgreementDocument) => void;
};

/**
 * The footer of links, on a remote surface and nowhere else.
 *
 * Returns nothing on a station, and the guard is inside the component rather
 * than at every call site. A footer is a row of links whose entire job is to
 * take somebody somewhere else, and a kiosk is a device with nowhere else to
 * go. Putting the condition here means a caller cannot forget it, and the same
 * screen code runs on both surfaces.
 */
export function LegalFooter({ documents, surface, onOpen }: LegalFooterProps): ReactNode {
  if (documentOpening(surface) !== "new_tab") return null;
  if (documents.length === 0) return null;

  return (
    <footer className="xp-legal-footer">
      {documents.map((document) => (
        <DocumentLink
          key={document.document_id}
          document={document}
          surface={surface}
          onOpen={onOpen}
        />
      ))}
    </footer>
  );
}
